import React, { useEffect, useRef, useState } from "react";
import {
  Mail,
  MailOpen,
  Trash2,
  Archive,
  Star,
  FolderInput,
  Tag,
  Copy,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { useContextMenuStore } from "../../stores/contextMenuStore";
import { useEmailStore } from "../../stores/emailStore";
import { useFolderStore } from "../../stores/folderStore";
import { useLabelStore } from "../../stores/labelStore";
import { useApi } from "../../hooks/useApi";
import { deleteEmailsWithUndo } from "../../utils/deleteWithUndo";

export default function EmailContextMenu() {
  const { isOpen, position, targetEmail, selectedEmails, closeMenu } = useContextMenuStore();
  const { markAsRead, markAsUnread, toggleFlag, moveEmails, addLabelToEmails, removeLabelFromEmails } = useEmailStore();
  const { folders, activeFolderId } = useFolderStore();
  const { labels } = useLabelStore();
  const { patch, del, post } = useApi();

  const menuRef = useRef<HTMLDivElement>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<"folder" | "label" | null>(null);
  const [submenuPos, setSubmenuPos] = useState<"right" | "left">("right");

  const emailsToProcess = selectedEmails.length > 0 ? selectedEmails : targetEmail ? [targetEmail] : [];
  const count = emailsToProcess.length;

  useEffect(() => {
    if (!isOpen) {
      setActiveSubmenu(null);
      return;
    }

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = "touches" in e ? (e as TouchEvent).touches[0]?.target : (e as MouseEvent).target;
      if (menuRef.current && target && !menuRef.current.contains(target as Node)) {
        closeMenu();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMenu();
      }
    };

    const handleScroll = () => {
      closeMenu();
    };

    window.addEventListener("mousedown", handlePointerDown as EventListener);
    window.addEventListener("touchstart", handlePointerDown as EventListener, { passive: true });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("mousedown", handlePointerDown as EventListener);
      window.removeEventListener("touchstart", handlePointerDown as EventListener);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, closeMenu]);

  // Adjust menu and submenu position so it doesn't overflow screen viewport
  const getCalculatedPosition = () => {
    const menuWidth = 230;
    const menuHeight = 290;
    const padding = 10;

    let x = position.x;
    let y = position.y;

    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding;
    }
    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding;
    }
    return { x: Math.max(padding, x), y: Math.max(padding, y) };
  };

  if (!isOpen || emailsToProcess.length === 0 || !targetEmail) return null;

  const calculatedPos = getCalculatedPosition();
  const isAllRead = emailsToProcess.every((e) => e.isRead);
  const isAllFlagged = emailsToProcess.every((e) => e.isFlagged);
  const ids = emailsToProcess.map((e) => e.id);
  const folder = activeFolderId || targetEmail.folderId || "INBOX";

  // Actions
  const handleToggleRead = async () => {
    const nextRead = !isAllRead;
    if (nextRead) {
      markAsRead(ids);
    } else {
      markAsUnread(ids);
    }
    closeMenu();
    for (const email of emailsToProcess) {
      patch(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}/flags`, {
        read: nextRead,
      }).catch((err) => {
        console.error("Error setting read flag:", err);
      });
    }
  };

  const handleToggleFlag = async () => {
    const nextFlagged = !isAllFlagged;
    for (const id of ids) {
      toggleFlag(id);
    }
    closeMenu();
    for (const email of emailsToProcess) {
      patch(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}/flags`, {
        flagged: nextFlagged,
      }).catch((err) => {
        console.error("Error setting flagged flag:", err);
      });
    }
  };

  const handleDelete = async () => {
    closeMenu();
    deleteEmailsWithUndo(
      emailsToProcess,
      () => {
        for (const email of emailsToProcess) {
          del(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}`).catch((err) => {
            console.error("Error deleting email:", err);
          });
        }
      },
      { message: `${count} correo(s) movido(s) a Papelera` }
    );
  };

  const handleArchive = async () => {
    moveEmails(ids, "Archive");
    closeMenu();
    for (const email of emailsToProcess) {
      patch(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}/move`, {
        destFolder: "Archive",
      }).catch((err) => {
        console.error("Error archiving email:", err);
      });
    }
  };

  const handleMoveToFolder = async (targetFolderId: string) => {
    moveEmails(ids, targetFolderId);
    closeMenu();
    for (const email of emailsToProcess) {
      patch(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}/move`, {
        destFolder: targetFolderId,
      }).catch((err) => {
        console.error(`Error moving email to ${targetFolderId}:`, err);
      });
    }
  };

  const handleToggleLabel = async (labelId: string) => {
    const hasLabel = emailsToProcess.every((e) => e.labels?.includes(labelId));
    if (hasLabel) {
      removeLabelFromEmails(ids, labelId);
    } else {
      addLabelToEmails(ids, labelId);
    }
    closeMenu();
    for (const email of emailsToProcess) {
      if (hasLabel) {
        del(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}/labels/${encodeURIComponent(labelId)}`).catch((err) => {
          console.error("Error removing label:", err);
        });
      } else {
        post(`/mail/mails/${encodeURIComponent(email.folderId || folder)}/${email.id}/labels`, {
          labelId,
        }).catch((err) => {
          console.error("Error adding label:", err);
        });
      }
    }
  };

  const handleCopySubject = () => {
    if (targetEmail) {
      navigator.clipboard.writeText(targetEmail.subject || "");
    }
    closeMenu();
  };

  return (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: `${calculatedPos.y}px`,
        left: `${calculatedPos.x}px`,
        zIndex: 99999,
      }}
      className="w-56 bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl py-1 select-none animate-in fade-in-0 zoom-in-95 duration-100 text-xs text-[var(--text-primary)]"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Header with selected count */}
      <div className="px-3 py-1.5 border-b border-[var(--border-subtle)] text-[10px] uppercase font-bold text-[var(--text-muted)] flex items-center justify-between">
        <span className="truncate">{count > 1 ? `${count} seleccionados` : targetEmail.from?.name || targetEmail.from?.email || "Correo"}</span>
      </div>

      {/* Marcar leído / no leído */}
      <button
        onClick={handleToggleRead}
        className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-accent hover:text-white transition-colors"
      >
        {isAllRead ? <Mail size={14} /> : <MailOpen size={14} />}
        <span>{isAllRead ? "Marcar como no leído" : "Marcar como leído"}</span>
      </button>

      {/* Destacar */}
      <button
        onClick={handleToggleFlag}
        className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-accent hover:text-white transition-colors"
      >
        <Star size={14} className={isAllFlagged ? "fill-current" : ""} />
        <span>{isAllFlagged ? "Quitar estrella" : "Destacar mensaje"}</span>
      </button>

      <div className="my-1 border-t border-[var(--border-subtle)]" />

      {/* Mover a carpeta (Submenú) */}
      <div
        className="relative"
        onMouseEnter={() => {
          setActiveSubmenu("folder");
          if (calculatedPos.x + 460 > window.innerWidth) setSubmenuPos("left");
          else setSubmenuPos("right");
        }}
        onMouseLeave={() => setActiveSubmenu(null)}
      >
        <button
          className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-accent hover:text-white transition-colors"
        >
          <span className="flex items-center gap-2.5">
            <FolderInput size={14} />
            <span>Mover a carpeta</span>
          </span>
          <ChevronRight size={12} />
        </button>

        {activeSubmenu === "folder" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              [submenuPos === "right" ? "left" : "right"]: "100%",
            }}
            className="w-48 bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl py-1 max-h-64 overflow-y-auto"
          >
            {folders
              .filter((f) => f.id !== folder)
              .map((f) => (
                <button
                  key={f.id}
                  onClick={() => handleMoveToFolder(f.id)}
                  className="w-full px-3 py-1.5 text-left flex items-center gap-2 hover:bg-accent hover:text-white transition-colors text-xs truncate"
                >
                  <span className="truncate">{f.name}</span>
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Etiquetas (Submenú) */}
      {labels.length > 0 && (
        <div
          className="relative"
          onMouseEnter={() => {
            setActiveSubmenu("label");
            if (calculatedPos.x + 460 > window.innerWidth) setSubmenuPos("left");
            else setSubmenuPos("right");
          }}
          onMouseLeave={() => setActiveSubmenu(null)}
        >
          <button
            className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-accent hover:text-white transition-colors"
          >
            <span className="flex items-center gap-2.5">
              <Tag size={14} />
              <span>Etiquetar</span>
            </span>
            <ChevronRight size={12} />
          </button>

          {activeSubmenu === "label" && (
            <div
              style={{
                position: "absolute",
                top: 0,
                [submenuPos === "right" ? "left" : "right"]: "100%",
              }}
              className="w-48 bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl py-1 max-h-64 overflow-y-auto"
            >
              {labels.map((l) => {
                const isTagged = emailsToProcess.every((e) => e.labels?.includes(l.id));
                return (
                  <button
                    key={l.id}
                    onClick={() => handleToggleLabel(l.id)}
                    className="w-full px-3 py-1.5 text-left flex items-center justify-between hover:bg-accent hover:text-white transition-colors text-xs"
                  >
                    <span className="flex items-center gap-2 truncate">
                      <span className="w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: l.color }} />
                      <span className="truncate">{l.name}</span>
                    </span>
                    {isTagged && <span className="text-2xs font-bold">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Archivar */}
      <button
        onClick={handleArchive}
        className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-accent hover:text-white transition-colors"
      >
        <Archive size={14} />
        <span>Archivar</span>
      </button>

      {/* Copiar asunto */}
      <button
        onClick={handleCopySubject}
        className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-accent hover:text-white transition-colors"
      >
        <Copy size={14} />
        <span>Copiar asunto</span>
      </button>

      <div className="my-1 border-t border-[var(--border-subtle)]" />

      {/* Eliminar */}
      <button
        onClick={handleDelete}
        className="w-full px-3 py-2 text-left flex items-center gap-2.5 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
      >
        <Trash2 size={14} />
        <span>Eliminar {count > 1 ? `(${count})` : ""}</span>
      </button>
    </div>
  );
}
